/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Render from '../render/Render';
import {shouldUseHeaderLogo} from '../theme/resellers/Resellers';
import {getExternalHeaderLogoImageURL, getExternalLogoImageURL} from '../theme/ThemeLoader';
import CalAmpAffiliateLogo from './CalAmpAffiliateLogo';

/**
 * The `PartnerLogo` component props accepts an optional `isHeader` prop which
 * should be set to true when the Logo is being displayed in the application
 * header, otherwise, defaults to false, which indicates that the logo is being
 * displayed in the default vertical mode (e.g. Login Page).
 */
export interface PartnerLogoProps {
  isHeader?: boolean;
  showAffiliateLogo?: boolean;
};

/**
 * Defines the styles used by the PartnerLogo component. The component contains
 * different layouts for the login page on desktop and mobile, as well as for
 * the desktop header.
 */
const useStyles = makeStyles((theme: any) => {
  return {
    container : ({isHeader}: PartnerLogoProps) => ({
      marginLeft                     : isHeader ? 16 : 0,
      flexDirection                  : isHeader ? 'initial' : 'column',
      marginBottom                   : !isHeader ? 10 : 0,
      display                        : 'flex',
      flexGrow                       : 1,
      height                         : 64,
      [theme.breakpoints.down('sm')] : {
        height : 'auto'
      }
    }),
    logo : {
      alignSelf : 'center',
      marginTop : 1,
      height    : 64
    },
    img : {
      padding                      : 0,
      margin                       : 0,
      [theme.breakpoints.up('sm')] : {
        width : ({isHeader}: PartnerLogoProps) => isHeader ? 188 : 330
      },
      [theme.breakpoints.down('sm')] : {
        width : 186
      }
    },
    calamp : ({isHeader}: PartnerLogoProps) => ({
      marginBottom                   : isHeader ? 12 : 0,
      marginLeft                     : isHeader ? -10 : 0,
      marginTop                      : 16,
      marginRight                    : 60,
      alignSelf                      : 'flex-end',
      [theme.breakpoints.down('sm')] : {
        position : 'absolute',
        top      : 40,
        margin   : 0
      }
    })
  };
});

/**
 * The `PartnerLogo` component displays an affiliate corporate logo in place of the
 * default CalAmp logo in a standardized way. This component is used for displaying
 * co-branded logos for customer re-sale of an application (white labelling).
 *
 * The `PartnerLogo` can be displayed in one of two modes; either in header mode,
 * in which case the CalAmp sub-logo is displayed horizontally, or in in normal
 * mode, in which case the CalAmp sub-logo is displayed vertically. Typically,
 * normal mode is for use on an application's login page, and header mode, as
 * the name implies, is displayed within the application header.
 *
 * ```typescript
 *   import PartnerLogo from '@ion/dls/src/logo/PartnerLogo';
 *   ...
 *
 *   <PartnerLogo isHeader />
 * ```
 *
 */
export const PartnerLogo = ({isHeader = false, showAffiliateLogo = true}: PartnerLogoProps) => {
  const [useHeaderLogo, setUseHeaderLogo] = React.useState(true);
  const classes        = useStyles({isHeader});
  const showHeaderLogo = isHeader && useHeaderLogo && shouldUseHeaderLogo();
  const src  = (
    showHeaderLogo ? getExternalHeaderLogoImageURL() : getExternalLogoImageURL()
  );

  const onLogoError = function() {
    if (showHeaderLogo) {
      setUseHeaderLogo(false);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.logo}>
        <img className={classes.img} src={src} onError={onLogoError} />
      </div>
      <Render.If condition={showAffiliateLogo}>
        <div className={classes.calamp}>
          <CalAmpAffiliateLogo isHeader={isHeader} />
        </div>
      </Render.If>
    </div>
  );
};

export default React.memo(PartnerLogo);
